import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Article, FloppyDisk, ArrowClockwise, Eye } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLUG_LABEL = {
  features: "Features",
  "live-data": "Live Data",
  about: "About",
  "research-papers": "Research Papers (intro copy)",
  methodology: "Methodology",
  "data-sources": "Data Sources",
  "contact-us": "Contact Us",
  newsletter: "Newsletter (intro copy)",
  partnerships: "Partnerships",
  careers: "Careers (intro copy)",
};

export default function PagesManager() {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const fetchAll = useCallback(async () => {
    const r = await axios.get(`${API}/admin/pages`, { withCredentials: true });
    setPages(r.data?.pages || []);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const open = (p) => {
    setSelected(p.slug);
    setDraft({
      title: p.title || "", subtitle: p.subtitle || "", body_html: p.body_html || "",
      contact_email: p.contact_email || "", contact_phone: p.contact_phone || "", contact_address: p.contact_address || "",
    });
    setMsg("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${selected}`, draft, { withCredentials: true });
      setMsg(`Saved "${SLUG_LABEL[selected] || selected}"`);
      fetchAll();
    } catch (e) { setMsg(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="pages-manager">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0">
          <Article size={18} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-white">Site Pages</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            Edit the content of every public page. Pages are rendered at <code className="text-emerald-400 font-mono">/{"{slug}"}</code>. Body accepts HTML — headings, paragraphs, lists, and links.
          </p>
        </div>
        <button onClick={fetchAll} className="text-xs text-stone-400 hover:text-white inline-flex items-center gap-1">
          <ArrowClockwise size={12} /> Reload
        </button>
      </div>

      {msg && (
        <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-lg px-3 py-2 text-xs text-emerald-300"
             data-testid="pages-msg">{msg}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-1.5">
          {pages.map((p) => (
            <button key={p.slug} onClick={() => open(p)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selected === p.slug ? "bg-emerald-600 text-white" : "bg-stone-900 hover:bg-stone-800 text-stone-300"}`}
              data-testid={`page-item-${p.slug}`}>
              <div className="flex items-center justify-between">
                <span className="truncate">{SLUG_LABEL[p.slug] || p.slug}</span>
                {p.is_seeded && <span className="text-[9px] px-1.5 py-0.5 rounded bg-stone-700/60 text-stone-400 font-bold uppercase ml-2 shrink-0">default</span>}
              </div>
              <p className="text-[10px] text-stone-500 mt-0.5">/{p.slug}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          {!selected && (
            <div className="bg-stone-900/40 rounded-2xl border border-stone-800/60 p-10 text-center">
              <p className="text-sm text-stone-400">Select a page on the left to edit.</p>
            </div>
          )}
          {selected && draft && (
            <div className="bg-stone-900 rounded-2xl border border-stone-800 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-base font-semibold text-white">{SLUG_LABEL[selected] || selected}</h3>
                <a href={`/${selected}`} target="_blank" rel="noreferrer"
                   className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                  <Eye size={12} /> Preview
                </a>
              </div>

              <div>
                <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Title</label>
                <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
                  data-testid={`page-title-${selected}`} />
              </div>
              <div>
                <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Subtitle</label>
                <input value={draft.subtitle} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                  className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
                  data-testid={`page-subtitle-${selected}`} />
              </div>
              <div>
                <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Body (HTML allowed)</label>
                <textarea value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                  rows={14}
                  className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-stone-100 font-mono focus:outline-none focus:border-emerald-500"
                  data-testid={`page-body-${selected}`} />
              </div>

              {selected === "contact-us" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-stone-800">
                  <div>
                    <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Contact email</label>
                    <input value={draft.contact_email} onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
                      placeholder="info@conexusfoundation.org"
                      className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                      data-testid="page-contact-email" />
                  </div>
                  <div>
                    <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Phone</label>
                    <input value={draft.contact_phone} onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
                      className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                      data-testid="page-contact-phone" />
                  </div>
                  <div>
                    <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Address</label>
                    <input value={draft.contact_address} onChange={(e) => setDraft({ ...draft, contact_address: e.target.value })}
                      className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                      data-testid="page-contact-address" />
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button onClick={save} disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg inline-flex items-center gap-2"
                  data-testid={`save-page-${selected}`}>
                  <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
